import { CSSProperties, memo, MouseEvent, ReactNode, useId, useRef, useState } from 'react';

import { setAbortableTimeout } from '@libs/abortableTimeout';
import { cx } from '@libs/classnames';
import { ContentEditableText } from '@ui/components/ContentEditableText/ContentEditableText';

import styles from './PopupControls.module.css';

/*
    ____,-------------------------------,____
    \   |        Simple Controls        |   /
    /___|-------------------------------|___\
*/

export type Option<T = string> = { title: string; value: T };

export const Label = memo(function Label({ children }: { children: ReactNode }) {
  return <label className={styles.label}>{children}</label>;
});

export const Slider = memo(function Slider({
  options,
  value,
  disabled,
  onChange,
}: {
  options: number[];
  value: number;
  disabled?: boolean;
  onChange?: (v: number) => void;
}) {
  const min = options[0]!;
  const max = options[options.length - 1]!;
  const percent = (options.indexOf(value) / (options.length - 1)) * 100;

  const findValue = (e: MouseEvent | PointerEvent) => {
    const target = e.target as HTMLDivElement;
    const rect = target.getBoundingClientRect();
    const v = e.clientX - rect.x;
    const w = rect.width;
    const p = v / w;
    const index = Math.round((options.length - 1) * p);
    return options[index]!;
  };

  const [dragging, setDragging] = useState(false);
  const acRef = useRef<AbortController | undefined>(undefined);

  return (
    <div
      className={cx(styles.slider, { [styles.is_disabled]: disabled })}
      style={{ '--_percent-a93f36ba': `${percent}%` } as CSSProperties}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        const target = e.target as HTMLDivElement;

        setDragging(true);
        acRef.current = new AbortController();
        setAbortableTimeout(
          () => {
            target.style.transition = 'none';
          },
          60,
          acRef.current.signal,
        );
        acRef.current.signal.addEventListener(
          'abort',
          () => {
            target.style.transition = '';
          },
          { once: true },
        );

        onChange?.(findValue(e));

        if (target.hasPointerCapture(e.pointerId)) {
          target.releasePointerCapture(e.pointerId);
        }
      }}
      onPointerUp={() => {
        setDragging(false);
        acRef.current?.abort();
      }}
      onPointerCancel={() => {
        setDragging(false);
        acRef.current?.abort();
      }}
      onPointerMove={(e) => {
        if (dragging) {
          onChange?.(findValue(e));
        }
      }}
    >
      <div className={styles.slider__mark}>{min}</div>
      <div className={styles.slider__value}>{value}</div>
      <div className={styles.slider__handle} />
      <div className={styles.slider__mark}>{max}</div>
    </div>
  );
});

function _Radio<T extends Option = Option>({
  options,
  selected,
  disabled,
  onChange,
}: {
  options: T[];
  selected: string;
  disabled?: boolean;
  onChange?: (o: T) => void;
}) {
  return (
    <div className={cx(styles.radio, { [styles.is_disabled]: disabled })}>
      {options.map((o) => {
        const active = o.value === selected;
        return (
          <div
            key={o.title}
            className={cx({
              [styles.is_opaque]: active,
              [styles.is_blurry]: !active,
              [styles.is_clickable]: !active,
            })}
            onClick={() => onChange?.(o)}
          >
            {o.title}
          </div>
        );
      })}
    </div>
  );
}
export const Radio = memo(_Radio) as typeof _Radio;

export const Toggle = memo(function Toggle({
  big,
  children,
  value,
  onChange,
}: {
  big?: boolean;
  children?: ReactNode;
  value?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div
      className={cx(styles.toggle, {
        [styles.is_blurry]: !value,
        [styles.is_opaque]: value,
        [styles.is_clickable]: !value,
        [styles.is_big]: big,
      })}
      onClick={() => onChange?.(!value)}
    >
      {children}
    </div>
  );
});

export const Button = memo(function Button({
  className,
  big,
  ghost,
  gold,
  customAlpha,
  children,
  disabled,
  onClick,
}: {
  className?: string;
  big?: boolean;
  ghost?: boolean;
  gold?: boolean;
  customAlpha?: 10 | 20;
  children?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className={cx(className, styles.button, {
        [styles.is_big]: big,
        [styles.is_ghost]: ghost,
        [styles.is_disabled]: disabled,
        [styles.is_gold]: gold,
      })}
      data-alpha={customAlpha}
      onClick={onClick}
    >
      {children}
    </div>
  );
});

export const RoundButton = memo(function RoundButton({
  children,
  disabled,
  onClick,
}: {
  children?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <div className={cx(styles.round_button, { [styles.is_disabled]: disabled })} onClick={onClick}>
      {children}
    </div>
  );
});

export const Input = memo(function Input({
  initialValue,
  prefix,
  postfix,
  notActive,
  onClick,
  onChange,
}: {
  initialValue: string;
  prefix?: string;
  postfix?: string;
  notActive?: boolean;
  onClick?: () => void;
  onChange?: (v: string) => void;
}) {
  const id = useId();

  return (
    <div
      className={cx(styles.input, {
        [styles.is_blurry]: notActive,
        [styles.is_opaque]: !notActive,
        [styles.is_clickable]: notActive,
      })}
      onClick={() => {
        if (!notActive) {
          document.getElementById(id)?.focus();
        }
        onClick?.();
      }}
    >
      {prefix}{' '}
      <ContentEditableText
        id={id}
        className={styles.input__editable}
        initialValue={initialValue}
        inputMode="numeric"
        maxLength={4}
        onChange={onChange}
      />{' '}
      {postfix}
    </div>
  );
});

function _VerticalRadio<T extends Option = Option>({
  className,
  centered,
  options,
  selected,
  onChange,
}: {
  className?: string;
  centered?: boolean;
  options: T[];
  selected?: string;
  onChange?: (o: T) => void;
}) {
  return (
    <div className={cx(styles.vertical_radio, className)}>
      {options.map((o) => {
        const active = o.value === selected;
        return (
          <div
            key={o.title}
            className={cx({
              [styles.is_opaque]: active,
              [styles.is_blurry]: !active,
              [styles.is_clickable]: !active,
              [styles.is_centered]: centered,
            })}
            onClick={() => onChange?.(o)}
          >
            {o.title}
          </div>
        );
      })}
    </div>
  );
}
export const VerticalRadio = memo(_VerticalRadio) as typeof _VerticalRadio;

export const MultilineInput = memo(function MultilineInput({
  className,
  autoFocus,
  initialValue,
  onChange,
}: {
  className?: string;
  autoFocus?: boolean;
  initialValue?: string;
  onChange?: (v: string) => void;
}) {
  return (
    <ContentEditableText
      autoFocus={autoFocus}
      initialValue={initialValue ?? ''}
      className={cx(styles.textarea, className)}
      onChange={onChange}
    />
  );
});

export const TextArea = memo(function TextArea({
  className,
  autoFocus,
  initialValue,
  onChange,
}: {
  className?: string;
  autoFocus?: boolean;
  initialValue?: string;
  onChange?: (v: string) => void;
}) {
  return (
    <textarea
      className={cx(styles.textarea, className)}
      onChange={(e) => onChange?.(e.target.value)}
      onFocus={(e) => e.target.select()}
      autoFocus={autoFocus}
      defaultValue={initialValue ?? ''}
    />
  );
});
